// SettingsPage - Application settings with tabs
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useThemeStore } from '@/stores/theme-store';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type SettingsTab = 'profile' | 'appearance' | 'notifications' | 'system';

const TABS: { id: SettingsTab; label: string; icon: string }[] = [
  {
    id: 'profile',
    label: 'settings.profileTab',
    icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  },
  {
    id: 'appearance',
    label: 'settings.appearanceTab',
    icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01',
  },
  {
    id: 'notifications',
    label: 'settings.notificationsTab',
    icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  },
  {
    id: 'system',
    label: 'settings.systemTab',
    icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  },
];

export function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '+36 30 123 4567',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Notification settings
  const [notifications, setNotifications] = useState({
    emailRentalExpiry: true,
    emailWorksheetStatus: true,
    emailNewPartner: false,
    pushEnabled: true,
    pushRentalExpiry: true,
    pushWorksheetComplete: true,
    dailySummary: true,
    weeklySummary: false,
  });

  // System settings
  const [systemSettings, setSystemSettings] = useState({
    language: 'hu',
    dateFormat: 'YYYY.MM.DD',
    currency: 'HUF',
    timezone: 'Europe/Budapest',
    autoLogout: '30',
    defaultRentalDays: '7',
    defaultDepositPercent: '20',
  });

  const handleSaveProfile = () => {
    alert(t('settings.profileSaved'));
  };

  const handleSaveNotifications = () => {
    alert(t('settings.notificationsSaved'));
  };

  const handleSaveSystem = () => {
    alert(t('settings.systemSaved'));
  };

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-400 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t('settings.title')}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.subtitleText')}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-4">
          {/* Sidebar with tabs */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {TABS.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors',
                        activeTab === tab.id
                          ? 'bg-primary text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                      )}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={tab.icon}
                        />
                      </svg>
                      {t(tab.label)}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>
          </div>

          {/* Content area */}
          <div className="lg:col-span-3 space-y-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>{t('settings.personalData')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary text-2xl font-bold text-white">
                        {user?.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <Button variant="outline" size="sm">
                          {t('settings.uploadImage')}
                        </Button>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {t('settings.avatarHelp')}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input
                        label={t('settings.fullName')}
                        value={profileForm.name}
                        onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                      />
                      <Input
                        label={t('settings.emailAddress')}
                        type="email"
                        value={profileForm.email}
                        onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                      />
                      <Input
                        label={t('settings.phoneNumber')}
                        value={profileForm.phone}
                        onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                      />
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.roleLabel')}
                        </label>
                        <input
                          type="text"
                          value={user?.role || 'Admin'}
                          disabled
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-gray-100 dark:bg-slate-700 px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSaveProfile}>{t('common.save')}</Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('settings.changePassword')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-3">
                      <Input
                        label={t('settings.currentPassword')}
                        type="password"
                        value={profileForm.currentPassword}
                        onChange={e =>
                          setProfileForm({ ...profileForm, currentPassword: e.target.value })
                        }
                      />
                      <Input
                        label={t('settings.newPassword')}
                        type="password"
                        value={profileForm.newPassword}
                        onChange={e =>
                          setProfileForm({ ...profileForm, newPassword: e.target.value })
                        }
                      />
                      <Input
                        label={t('settings.confirmNewPassword')}
                        type="password"
                        value={profileForm.confirmPassword}
                        onChange={e =>
                          setProfileForm({ ...profileForm, confirmPassword: e.target.value })
                        }
                      />
                    </div>

                    <div className="flex justify-end">
                      <Button variant="outline" onClick={handleSaveProfile}>
                        {t('settings.changePasswordButton')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Appearance Tab */}
            {activeTab === 'appearance' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('settings.appearanceTab')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <label className="mb-3 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('settings.themeLabel')}
                    </label>
                    <div className="grid gap-4 sm:grid-cols-3">
                      {[
                        {
                          id: 'light',
                          label: t('settings.themeLight'),
                          icon: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
                        },
                        {
                          id: 'dark',
                          label: t('settings.themeDark'),
                          icon: 'M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z',
                        },
                        {
                          id: 'system',
                          label: t('settings.systemTab'),
                          icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                        },
                      ].map(option => (
                        <button
                          key={option.id}
                          onClick={() => setTheme(option.id as 'light' | 'dark' | 'system')}
                          className={cn(
                            'flex flex-col items-center gap-3 rounded-lg border-2 p-4 transition-colors',
                            theme === option.id
                              ? 'border-primary bg-primary/10 dark:bg-primary/20'
                              : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                          )}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className={cn(
                              'h-8 w-8',
                              theme === option.id
                                ? 'text-primary'
                                : 'text-gray-400 dark:text-gray-300'
                            )}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={option.icon}
                            />
                          </svg>
                          <span
                            className={cn(
                              'font-medium',
                              theme === option.id
                                ? 'text-primary'
                                : 'text-gray-700 dark:text-gray-300'
                            )}
                          >
                            {option.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg bg-gray-50 dark:bg-slate-700/50 p-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {t('common.details')}
                    </h4>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="rounded-lg bg-white dark:bg-slate-800 p-4 shadow-sm">
                        <div className="h-4 w-3/4 rounded bg-gray-200 dark:bg-slate-600 mb-2"></div>
                        <div className="h-3 w-1/2 rounded bg-gray-100 dark:bg-slate-700"></div>
                      </div>
                      <div className="rounded-lg bg-primary/10 dark:bg-primary/20 p-4">
                        <div className="h-4 w-2/3 rounded bg-primary/30 mb-2"></div>
                        <div className="h-3 w-1/3 rounded bg-primary/20"></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('settings.notificationsTab')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                      {t('settings.emailNotifications')}
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          key: 'emailRentalExpiry',
                          label: t('settings.notifRentalExpiry'),
                          desc: t('settings.rentalExpiryDesc'),
                        },
                        {
                          key: 'emailWorksheetStatus',
                          label: t('settings.notifServiceReady'),
                          desc: t('settings.notifServiceReadyDesc'),
                        },
                        {
                          key: 'emailNewPartner',
                          label: t('settings.notifNewBooking'),
                          desc: t('settings.notifNewBookingDesc'),
                        },
                      ].map(item => (
                        <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              notifications[item.key as keyof typeof notifications] as boolean
                            }
                            onChange={e =>
                              setNotifications({ ...notifications, [item.key]: e.target.checked })
                            }
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.label}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="border-t dark:border-slate-600 pt-6">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                      {t('settings.pushNotifications')}
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          key: 'pushEnabled',
                          label: t('settings.notifPush'),
                          desc: t('settings.notifPushDesc'),
                        },
                        {
                          key: 'pushRentalExpiry',
                          label: t('settings.notifRentalExpiry'),
                          desc: t('settings.notifRentalExpiryDesc'),
                        },
                        {
                          key: 'pushWorksheetComplete',
                          label: t('settings.notifServiceReady'),
                          desc: t('settings.notifServiceReadyDesc'),
                        },
                      ].map(item => (
                        <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              notifications[item.key as keyof typeof notifications] as boolean
                            }
                            onChange={e =>
                              setNotifications({ ...notifications, [item.key]: e.target.checked })
                            }
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.label}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="border-t dark:border-slate-600 pt-6">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4">
                      {t('settings.summaryEmails')}
                    </h4>
                    <div className="space-y-3">
                      {[
                        {
                          key: 'dailySummary',
                          label: t('settings.notifEmail'),
                          desc: t('settings.dailySummaryDesc'),
                        },
                        {
                          key: 'weeklySummary',
                          label: t('settings.notifSound'),
                          desc: t('settings.weeklySummaryDesc'),
                        },
                      ].map(item => (
                        <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              notifications[item.key as keyof typeof notifications] as boolean
                            }
                            onChange={e =>
                              setNotifications({ ...notifications, [item.key]: e.target.checked })
                            }
                            className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-slate-600 text-primary focus:ring-primary"
                          />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-gray-100">
                              {item.label}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveNotifications}>{t('common.save')}</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* System Tab */}
            {activeTab === 'system' && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle>{t('settings.systemSettings')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.languageLabel')}
                        </label>
                        <select
                          value={systemSettings.language}
                          onChange={e =>
                            setSystemSettings({ ...systemSettings, language: e.target.value })
                          }
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="hu">Magyar</option>
                          <option value="en">English</option>
                          <option value="de">Deutsch</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.dateFormatLabel')}
                        </label>
                        <select
                          value={systemSettings.dateFormat}
                          onChange={e =>
                            setSystemSettings({ ...systemSettings, dateFormat: e.target.value })
                          }
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="YYYY.MM.DD">2026.01.18</option>
                          <option value="DD.MM.YYYY">18.01.2026</option>
                          <option value="MM/DD/YYYY">01/18/2026</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.currencyLabel')}
                        </label>
                        <select
                          value={systemSettings.currency}
                          onChange={e =>
                            setSystemSettings({ ...systemSettings, currency: e.target.value })
                          }
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="HUF">{t('settings.currencyHUF')}</option>
                          <option value="EUR">{t('settings.currencyEUR')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.timezoneLabel')}
                        </label>
                        <select
                          value={systemSettings.timezone}
                          onChange={e =>
                            setSystemSettings({ ...systemSettings, timezone: e.target.value })
                          }
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="Europe/Budapest">Europe/Budapest (CET/CEST)</option>
                          <option value="UTC">UTC</option>
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('settings.securityTitle')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.autoLogout')} {t('settings.minutes')}
                        </label>
                        <select
                          value={systemSettings.autoLogout}
                          onChange={e =>
                            setSystemSettings({ ...systemSettings, autoLogout: e.target.value })
                          }
                          className="flex h-10 w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          <option value="15">{t('settings.autoLogout15')}</option>
                          <option value="30">{t('settings.autoLogout30')}</option>
                          <option value="60">{t('settings.autoLogout60')}</option>
                          <option value="120">{t('settings.autoLogout120')}</option>
                          <option value="never">{t('settings.autoLogoutNever')}</option>
                        </select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>{t('settings.rentalDefaultsTitle')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.defaultRentalDays')}
                        </label>
                        <Input
                          type="number"
                          value={systemSettings.defaultRentalDays}
                          onChange={e =>
                            setSystemSettings({
                              ...systemSettings,
                              defaultRentalDays: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('settings.defaultDeposit')}
                        </label>
                        <Input
                          type="number"
                          value={systemSettings.defaultDepositPercent}
                          onChange={e =>
                            setSystemSettings({
                              ...systemSettings,
                              defaultDepositPercent: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button onClick={handleSaveSystem}>{t('common.save')}</Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-red-200 dark:border-red-800">
                  <CardHeader>
                    <CardTitle className="text-red-600 dark:text-red-400">
                      {t('settings.dangerZone')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {t('settings.exportButton')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('settings.exportDesc')}
                        </p>
                      </div>
                      <Button variant="outline">{t('common.export')}</Button>
                    </div>
                    <div className="flex items-center justify-between border-t dark:border-slate-600 pt-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {t('settings.clearCache')}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {t('settings.clearCacheDesc')}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                      >
                        {t('settings.clearButton')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
